import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { AuthState, User, LoginCredentials } from '@/types/auth';
import { authService } from '@/services/auth';

interface AuthStore extends AuthState {
  // Actions
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  loadUserFromStorage: () => void;
  updateUser: (user: User) => void;
  clearError: () => void;
  checkPermission: (permission: string) => boolean;
  checkRole: (role: string) => boolean;
  checkAnyRole: (roles: string[]) => boolean;
  checkModuleAccess: (moduleId: string) => boolean;
}

export const useAuthStore = create<AuthStore>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,

        // Actions
        login: async (credentials: LoginCredentials) => {
          set({ isLoading: true, error: null }, false, 'auth/login/start');

          try {
            const response = await authService.login(credentials);

            if (response.success && response.data) {
              set(
                {
                  user: response.data.user,
                  isAuthenticated: true,
                  isLoading: false,
                  error: null,
                },
                false,
                'auth/login/success'
              );
            } else {
              set(
                {
                  user: null,
                  isAuthenticated: false,
                  isLoading: false,
                  error: response.error?.message ?? 'Login failed',
                },
                false,
                'auth/login/error'
              );
              throw new Error(response.error?.message ?? 'Login failed');
            }
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
            set(
              {
                user: null,
                isAuthenticated: false,
                isLoading: false,
                error: errorMessage,
              },
              false,
              'auth/login/error'
            );
            throw error;
          }
        },

        logout: async () => {
          set({ isLoading: true }, false, 'auth/logout/start');

          try {
            await authService.logout();
          } catch (error) {
            console.warn('Logout API call failed:', error);
          } finally {
            set(
              {
                user: null,
                isAuthenticated: false,
                isLoading: false,
                error: null,
              },
              false,
              'auth/logout/complete'
            );
          }
        },

        refreshToken: async () => {
          try {
            const response = await authService.refreshToken();

            if (!response.success) {
              // Refresh failed, logout user
              void get().logout();
              throw new Error(response.error?.message ?? 'Token refresh failed');
            }
          } catch (error) {
            console.error('Token refresh failed:', error);
            void get().logout();
            throw error;
          }
        },

        loadUserFromStorage: () => {
          // Don't override if already authenticated (prevents race conditions)
          const currentState = get();
          if (currentState.isAuthenticated) {
            return;
          }

          const isAuthenticated = authService.isAuthenticated();
          const user = authService.getStoredUser();

          if (isAuthenticated && user) {
            set(
              {
                user,
                isAuthenticated: true,
                isLoading: false,
                error: null,
              },
              false,
              'auth/loadFromStorage'
            );
          } else {
            set(
              {
                user: null,
                isAuthenticated: false,
                isLoading: false,
                error: null,
              },
              false,
              'auth/loadFromStorage/empty'
            );
          }
        },

        updateUser: (user: User) => {
          set({ user }, false, 'auth/updateUser');
        },

        clearError: () => {
          set({ error: null }, false, 'auth/clearError');
        },

        checkPermission: (permission: string) => {
          return authService.hasPermission(permission);
        },

        checkRole: (role: string) => {
          return authService.hasRole(role);
        },

        checkAnyRole: (roles: string[]) => {
          return authService.hasAnyRole(roles);
        },

        checkModuleAccess: (moduleId: string) => {
          const state = get();
          if (!state.user) {return false;}
          // SUPER_ADMIN has access to all modules
          if (state.user.roles.some(r => r.name === 'SUPER_ADMIN')) {return true;}
          return state.user.moduleAccess?.includes(moduleId) ?? false;
        },
      }),
      {
        name: 'auth-store',
        partialize: (state) => ({
          // Only persist user and authentication status
          user: state.user,
          isAuthenticated: state.isAuthenticated,
        }),
        onRehydrateStorage: () => (state) => {
          // Validate stored data on rehydration
          if (state) {
            const isStillValid = authService.isAuthenticated();
            if (!isStillValid) {
              state.user = null;
              state.isAuthenticated = false;
            }
          }
        },
      }
    ),
    {
      name: 'auth-store',
    }
  )
);

// Selector hooks for better performance
export const useAuth = () => useAuthStore((state) => ({
  user: state.user,
  isAuthenticated: state.isAuthenticated,
  isLoading: state.isLoading,
  error: state.error,
}));

export const useAuthActions = () => useAuthStore((state) => ({
  login: state.login,
  logout: state.logout,
  refreshToken: state.refreshToken,
  loadUserFromStorage: state.loadUserFromStorage,
  updateUser: state.updateUser,
  clearError: state.clearError,
  checkPermission: state.checkPermission,
  checkRole: state.checkRole,
  checkAnyRole: state.checkAnyRole,
  checkModuleAccess: state.checkModuleAccess,
}));